import { theme } from "antd";
import { ContentViews } from "pages/[orgId]/explore/lib/useContentView";
import { CSSProperties } from "react";
import { useColorScheme } from "src/hooks/useColorScheme";
import { BarchartIcon } from "./BarchartIcon";
import { HeatmapIcon } from "./HeatmapIcon";
import { MetricIcon } from "./MetricIcon";
import { PivotTableIcon } from "./PivotTableIcon";
import { TableIcon } from "./TableIcon";
import { TimelineIcon } from "./TimelineIcon";
import { TreemapIcon } from "./TreemapIcon";
import {
  AreaChartOutlined,
  BarChartOutlined,
  FontSizeOutlined,
  LineChartOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { VerticalBarchartIcon } from "./VerticalBarchartIcon";

export function VizIcon(props: {
  inactive?: boolean;
  value: string;
  size?: number;
  style?: CSSProperties;
  config?: {
    displayType?: string;
    vertical?: boolean;
  };
  ignoreCssOverride?: boolean;
}) {
  const { token } = theme.useToken();
  const scheme = useColorScheme();
  const cssOverride: CSSProperties = {
    ...props.style,
    ...(scheme === "dark"
      ? { backgroundColor: token.colorBgContainer, borderRadius: 1, padding: 1 }
      : {}),
  };
  const size = props.size === undefined ? 16 : props.size;

  // Define theme-based colors for the default SVG
  const defaultColors = {
    light: token.colorTextSecondary,
    medium: token.colorTextTertiary,
    dark: token.colorText,
    accent: token.colorPrimary,
  };

  if (props.value === ContentViews.barChart) {
    if (props.config?.displayType === "lines") {
      return (
        <LineChartOutlined
          style={{ fontSize: size }}
          width={size}
          height={size}
        />
      );
    }

    if (props.config?.displayType === "area") {
      return (
        <AreaChartOutlined
          style={{ fontSize: size }}
          width={size}
          height={size}
        />
      );
    }

    if (props.config?.displayType === "bars" && props.config?.vertical) {
      return (
        <VerticalBarchartIcon
          style={cssOverride}
          inactive={props.inactive}
          size={size}
        />
      );
    }

    return (
      <BarchartIcon style={cssOverride} inactive={props.inactive} size={size} />
    );
  }

  if (props.value === ContentViews.labels)
    return (
      <FontSizeOutlined style={{ fontSize: size }} width={size} height={size} />
    );
  if (props.value === ContentViews.legend)
    return (
      <svg
        className="layout-control-button-icon"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="4" y="6" width="3" height="3" rx="0.5" fill="currentColor" />
        <rect x="4" y="11" width="3" height="3" rx="0.5" fill="currentColor" />
        <rect x="4" y="16" width="3" height="3" rx="0.5" fill="currentColor" />
        <path
          d="M9 7.5h11M9 12.5h11M9 17.5h11"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );

  if (props.value === ContentViews.heatMap)
    return (
      <HeatmapIcon style={cssOverride} inactive={props.inactive} size={size} />
    );
  if (props.value === ContentViews.treeMap)
    return (
      <TreemapIcon style={cssOverride} inactive={props.inactive} size={size} />
    );
  if (props.value === ContentViews.overTime)
    return (
      <TimelineIcon style={cssOverride} inactive={props.inactive} size={size} />
    );
  if (props.value === ContentViews.metric)
    return (
      <MetricIcon style={cssOverride} inactive={props.inactive} size={size} />
    );
  if (props.value === ContentViews.dataTable)
    return (
      <TableIcon style={cssOverride} inactive={props.inactive} size={size} />
    );
  if (props.value === ContentViews.pivotTable)
    return (
      <PivotTableIcon
        style={cssOverride}
        inactive={props.inactive}
        size={size}
      />
    );
  if (props.value === ContentViews.unifiedTable)
    return (
      <TableOutlined
        style={{ fontSize: size, ...cssOverride }}
        width={size}
        height={size}
      />
    );
  return (
    <svg
      style={{ display: "block", ...cssOverride }}
      width={size}
      height={(size * 53) / 88}
      viewBox="0 0 88 53"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        width="8"
        height="50"
        transform="translate(0 3)"
        fill={defaultColors.light}
      />
      <rect
        width="8"
        height="39"
        transform="translate(10 14)"
        fill={defaultColors.medium}
      />
      <rect
        width="8"
        height="26"
        transform="translate(20 27)"
        fill={defaultColors.dark}
      />
      <circle cx="39" cy="42" r="8" fill={defaultColors.dark} />
      <circle cx="53.5" cy="38.5" r="4.5" fill={defaultColors.medium} />
      <circle cx="55.5" cy="5.5" r="2.5" fill={defaultColors.dark} />
      <circle cx="50.5" cy="47.5" r="2.5" fill={defaultColors.medium} />
      <circle cx="44.5" cy="19.5" r="13.5" fill={defaultColors.light} />
      <g clipPath="url(#clip0_1388_73533)">
        <line
          x1="63.558"
          y1="59.3683"
          x2="114.891"
          y2="-3.63167"
          stroke={defaultColors.medium}
          strokeWidth="2"
        />
        <line
          x1="57.2246"
          y1="59.3683"
          x2="108.558"
          y2="-3.63167"
          stroke={defaultColors.dark}
          strokeWidth="2"
        />
        <line
          x1="50.8913"
          y1="59.3683"
          x2="102.225"
          y2="-3.63167"
          stroke={defaultColors.dark}
          strokeWidth="2"
        />
        <line
          x1="44.558"
          y1="59.3683"
          x2="95.8913"
          y2="-3.63167"
          stroke={defaultColors.light}
          strokeWidth="2"
        />
        <line
          x1="38.2247"
          y1="59.3683"
          x2="89.558"
          y2="-3.63167"
          stroke={defaultColors.medium}
          strokeWidth="2"
        />
        <line
          x1="31.8914"
          y1="59.3683"
          x2="83.2247"
          y2="-3.63167"
          stroke={defaultColors.medium}
          strokeWidth="2"
        />
        <line
          x1="25.5581"
          y1="59.3683"
          x2="76.8914"
          y2="-3.63167"
          stroke={defaultColors.dark}
          strokeWidth="2"
        />
        <line
          x1="19.2248"
          y1="59.3683"
          x2="70.5581"
          y2="-3.63167"
          stroke={defaultColors.dark}
          strokeWidth="2"
        />
      </g>
      <rect
        width="12"
        height="7.16667"
        transform="translate(76)"
        fill={defaultColors.light}
      />
      <rect
        width="12"
        height="7.16667"
        transform="translate(76 9.16602)"
        fill={defaultColors.medium}
      />
      <rect
        width="12"
        height="7.16667"
        transform="translate(76 18.334)"
        fill={defaultColors.dark}
      />
      <rect
        width="12"
        height="7.16667"
        transform="translate(76 27.5)"
        fill={defaultColors.medium}
      />
      <rect
        width="12"
        height="7.16667"
        transform="translate(76 36.666)"
        fill={defaultColors.medium}
      />
      <rect
        width="12"
        height="7.16667"
        transform="translate(76 45.834)"
        fill={defaultColors.light}
      />
      <defs>
        <clipPath id="clip0_1388_73533">
          <rect width="12" height="53" fill="white" transform="translate(61)" />
        </clipPath>
      </defs>
    </svg>
  );
}
